import React, { useEffect } from "react";
import Likert, {
  Scale as LikertScale,
  LikertFollowUpMultiSelectAnswer,
} from "../components/likert";
import { QuestionProps } from "../../survey";
import LikertQuestionWrapper, {
  FollowUpLowQuestionProps,
} from "../../../components/base_ui/higher_order_components/questions/likert_question_wrapper";
import {
  MultiSelectFollowUpQuestionType,
  MultiSelectOption,
} from "../../../components/base_ui/higher_order_components/questions/multiselect_question_wrapper";
import { Option } from "../question_helpers";
import {
  LikertCustomizationsNormalized,
  useNormalizeSurveyQuestionLikertCustomizations,
} from "./use_normalize_survey_question_likert_customizations";
import {
  LIKERT_FOLLOW_UP_CHARACTER_LIMIT,
  LIKERT_MULTI_SELECT_OTHER_OPTION_FOLLOW_UP_CHARACTER_LIMIT,
  useSurveyQuestionLikertValidation,
} from "./use_survey_question_likert_validation";

export interface Answer {
  value: LikertScale;
  followUpResponse?: string | LikertFollowUpMultiSelectAnswer;
  followUpLowResponse?: string | LikertFollowUpMultiSelectAnswer;
  followUpHighResponse?: string | LikertFollowUpMultiSelectAnswer;
}

export interface FollowUpLowQuestionOption {
  oid: string;
  text: string;
}

export interface Customizations {
  prompt: string;
  followUpPrompt?: string;
  followUpScore?: boolean;
  followUpLowScore?: boolean;
  followUpLowPrompt?: string;
  followUpLowDescription?: string;
  followUpLowQuestion?: {
    type: string;
    options: FollowUpLowQuestionOption[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [propName: string]: any;
  };
  followUpHighScore: boolean;
  followUpHighPrompt?: string;
  max?: LikertScale;
  maxLabel?: string;
  min?: LikertScale;
  minLabel?: string;
  displayValues?: string[] | number[];
  optional?: boolean;
  lowScoreThreshold?: number;
  highScoreThreshold?: number;
  description?: string;
  hideForKeyValuePairsOr?: { [key: string]: boolean };
}

export default function SurveyQuestionLikert({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
  dataTest = "survey-question-likert",
}: QuestionProps<Answer, Customizations>) {
  const { value, followUpLowResponse, followUpHighResponse } = currentAnswer || {};
  const followUpLowMultiSelectResponse =
    typeof followUpLowResponse !== "string" && followUpLowResponse?.standard !== undefined
      ? followUpLowResponse
      : undefined;

  const {
    label,
    description,
    followUpLowDescriptionLabel,
    followUpLowScore,
    followUpLowQuestion,
    followUpLowScoreThreshold,
    followUpLowPrompt,
    followUpHighScore,
    followUpHighScoreThreshold,
    followUpHighPrompt,
    max,
    maxLabel,
    minLabel,
    min,
    displayValues,
    optional,
    followUpLowScoreIsOptional,
    followUpHighScoreIsOptional,
  }: LikertCustomizationsNormalized =
    useNormalizeSurveyQuestionLikertCustomizations(customizations);

  const valueTriggersLowScoreFollowUp = (value?: LikertScale) => {
    return (
      followUpLowScore === true && value !== undefined && +value <= (followUpLowScoreThreshold ?? 0)
    );
  };
  const valueTriggersHighScoreFollowUp = (value?: LikertScale) => {
    return (
      followUpHighScore === true &&
      value !== undefined &&
      +value >= (followUpHighScoreThreshold ?? 0)
    );
  };
  const triggeredLowScoreFollowUp = valueTriggersLowScoreFollowUp(value);
  const triggeredHighScoreFollowUp = valueTriggersHighScoreFollowUp(value);

  const {
    deprecatedAnswerIsValid,
    answerIsValid,
    followUpIsValid,
    ableToSubmit,
    maximumCharacterLimitExceededBy,
    followUpCurrentCharacterCount,
    maximumCharacterLimitToRespect,
  } = useSurveyQuestionLikertValidation(customizations, currentAnswer, {
    triggeredLowScoreFollowUp,
    triggeredHighScoreFollowUp,
  });

  const followUpIsOptional = triggeredLowScoreFollowUp
    ? followUpLowScoreIsOptional
    : triggeredHighScoreFollowUp
      ? followUpHighScoreIsOptional
      : true;

  const requiredErrorMessage = "Please complete the required field.";
  const characterLimitErrorMessage =
    maximumCharacterLimitExceededBy === 1
      ? `Maximum character limit exceeded by ${maximumCharacterLimitExceededBy} character.`
      : `Maximum character limit exceeded by ${maximumCharacterLimitExceededBy} characters.`;

  useEffect(() => {
    if (showRefreshedComponents) {
      setAbleToSubmit(ableToSubmit);
    } else {
      setAbleToSubmit(deprecatedAnswerIsValid);
    }
  }, [deprecatedAnswerIsValid, ableToSubmit, setAbleToSubmit, showRefreshedComponents]);

  // Transform data for the new component
  const displayValuesToFormat = displayValues ? displayValues : [];
  const displayValuesFormattedForLikert = !displayValues
    ? undefined
    : Object.fromEntries(displayValuesToFormat.map((v, index) => [index + min, v]));
  const followUpLowQuestionOptions = followUpLowQuestion
    ? [
        ...(followUpLowQuestion.options as FollowUpLowQuestionOption[]).map(
          (option: FollowUpLowQuestionOption) => ({
            id: option.oid,
            label: option.text,
            value: false,
          }),
        ),
        ...(followUpLowQuestion.withOtherOption
          ? [
              {
                id: followUpLowQuestion.withOtherOption.oid,
                label: followUpLowQuestion.withOtherOption.text,
                value: false,
                followup: {
                  optional: true,
                  type: MultiSelectFollowUpQuestionType.TEXT_INPUT,
                  value: "",
                  maxCharacters: LIKERT_MULTI_SELECT_OTHER_OPTION_FOLLOW_UP_CHARACTER_LIMIT,
                },
              },
            ]
          : []),
      ]
    : [];

  const getValueForMultiSelectOption = (option: (typeof followUpLowQuestionOptions)[0]) =>
    followUpLowMultiSelectResponse?.standard?.find(
      (selectedOption) => (selectedOption as Option)?.oid === option.id,
    )
      ? true
      : false;

  // If we're dealing with a multi-select flup, the shape of the followUpLowResponse is not a string, instead:
  // followUpLowResponse: { standard: [{ oid: something, text: "Something", optional: boolean }], writeIn: undefined | "something" }
  const followUpLowQuestionFormattedForLikert = followUpLowQuestion
    ? {
        onChange: (newResponse: MultiSelectOption) => {
          const newResponseHasWriteIn = newResponse.id === followUpLowQuestion.withOtherOption?.oid;
          const writeIn = newResponseHasWriteIn ? newResponse?.followup?.value : undefined;
          return currentAnswer && value !== undefined
            ? updateAnswer({
                ...currentAnswer,
                followUpLowResponse:
                  triggeredLowScoreFollowUp && followUpLowQuestionOptions.length > 0
                    ? {
                        standard: followUpLowQuestionOptions
                          .map((option) =>
                            option.id === newResponse.id
                              ? { ...option, ...newResponse, oid: option.id, text: option.label }
                              : {
                                  ...option,
                                  oid: option.id,
                                  text: option.label,
                                  value: getValueForMultiSelectOption(option),
                                },
                          )
                          .filter((option) => option.value),
                        writeIn,
                      }
                    : undefined,
              })
            : undefined;
        },
        setAbleToSubmit: () => {
          /* No-op. We don't currently support required follow ups for this question. */
        },
        type: followUpLowQuestion.type,
        showErrors: (!followUpIsValid && showValidation) || maximumCharacterLimitExceededBy > 0,
        optional: followUpLowQuestion.optional,
        options: followUpLowQuestionOptions.map((option) => ({
          ...option,
          value: getValueForMultiSelectOption(option),
          followup:
            option.id === followUpLowQuestion.withOtherOption?.oid
              ? {
                  ...(option as MultiSelectOption).followup,
                  value: followUpLowMultiSelectResponse?.writeIn,
                }
              : undefined,
        })),
      }
    : undefined;

  const deprecatedLikert = (
    <div className="likert">
      {followUpLowScore || followUpHighScore ? (
        <Likert
          value={currentAnswer ? currentAnswer.value : undefined}
          prompt={label}
          max={max}
          min={min}
          maxLabel={customizations.maxLabel}
          minLabel={customizations.minLabel}
          optional={optional}
          displayValues={displayValues}
          description={description}
          onChangeValue={(newValue) => {
            let answer = currentAnswer;
            if (
              answer?.followUpHighResponse &&
              typeof customizations.lowScoreThreshold !== "undefined" &&
              newValue >= customizations?.lowScoreThreshold
            ) {
              const { followUpHighResponse, ...withoutFollowUpHighResponse } = answer;
              answer = withoutFollowUpHighResponse;
            }

            if (
              answer?.followUpLowResponse &&
              typeof customizations.highScoreThreshold !== "undefined" &&
              newValue >= customizations?.highScoreThreshold
            ) {
              const { followUpLowResponse, ...withoutFollowUpLowResponse } = answer;
              answer = withoutFollowUpLowResponse;
            }
            updateAnswer({ ...answer, value: newValue });
          }}
          followUpLowScore={followUpLowScore}
          followUpLowPrompt={customizations.followUpLowPrompt}
          followUpLowDescription={followUpLowDescriptionLabel}
          followUpLowResponse={
            currentAnswer && currentAnswer.followUpLowResponse
              ? currentAnswer.followUpLowResponse
              : ""
          }
          followUpLowQuestion={customizations.followUpLowQuestion}
          lowScoreThreshold={customizations.lowScoreThreshold}
          onChangeLowFollowUp={(newResponse) => {
            if (!currentAnswer)
              throw new Error("Attempted to update NPS low score follow up without a score");
            updateAnswer({ ...currentAnswer, followUpLowResponse: newResponse });
          }}
          followUpHighScore={followUpHighScore}
          followUpHighPrompt={customizations.followUpHighPrompt}
          followUpHighResponse={
            currentAnswer && currentAnswer.followUpHighResponse
              ? currentAnswer.followUpHighResponse
              : ""
          }
          highScoreThreshold={customizations.highScoreThreshold}
          onChangeHighFollowUp={(newResponse) => {
            if (!currentAnswer)
              throw new Error("Attempted to update NPS low score follow up without a score");

            updateAnswer({
              ...currentAnswer,
              followUpHighResponse: newResponse,
            });
          }}
        />
      ) : (
        <Likert
          value={currentAnswer ? currentAnswer.value : undefined}
          prompt={label}
          max={max}
          min={min}
          maxLabel={customizations.maxLabel}
          minLabel={customizations.minLabel}
          optional={optional}
          displayValues={displayValues}
          description={description}
          onChangeValue={(newValue) =>
            updateAnswer(
              currentAnswer ? { ...currentAnswer, value: newValue } : { value: newValue },
            )
          }
          followUpLowScore={false}
          followUpHighScore={false}
        />
      )}
    </div>
  );

  const likert = (
    <LikertQuestionWrapper
      optional={optional}
      prompt={label}
      description={description}
      displayValues={displayValuesFormattedForLikert}
      max={max}
      maxLabel={maxLabel}
      min={min}
      minLabel={minLabel}
      value={value !== undefined ? value.toString() : undefined}
      onChange={(e) => {
        updateAnswer({
          followUpLowResponse:
            triggeredLowScoreFollowUp &&
            valueTriggersLowScoreFollowUp(+e.target.value as LikertScale)
              ? followUpLowResponse
              : undefined,
          followUpHighResponse:
            triggeredHighScoreFollowUp &&
            valueTriggersHighScoreFollowUp(+e.target.value as LikertScale)
              ? followUpHighResponse
              : undefined,
          value: +e.target.value as LikertScale,
        });
      }}
      followUpMaxCharacterCount={LIKERT_FOLLOW_UP_CHARACTER_LIMIT}
      followUpCurrentCharacterCount={followUpCurrentCharacterCount}
      followUpLowScore={followUpLowScore}
      followUpLowPrompt={followUpLowPrompt}
      followUpLowQuestion={followUpLowQuestionFormattedForLikert as FollowUpLowQuestionProps}
      followUpLowDescription={followUpLowDescriptionLabel}
      followUpLowResponse={followUpLowResponse as string}
      lowScoreThreshold={followUpLowScoreThreshold}
      followUpIsOptional={followUpIsOptional}
      followUpHighScore={followUpHighScore}
      followUpHighPrompt={followUpHighPrompt}
      followUpHighResponse={followUpHighResponse as string}
      highScoreThreshold={followUpHighScoreThreshold}
      // This is the handler for open response follow ups. Multi-select follow ups are handled
      // separately, see above.
      onChangeFollowUp={(e) => {
        if (!currentAnswer || !value)
          throw new Error("Attempted to update low/high score follow up without a score");

        updateAnswer({
          ...currentAnswer,
          followUpHighResponse: triggeredHighScoreFollowUp ? e.target.value : undefined,
          followUpLowResponse: triggeredLowScoreFollowUp ? e.target.value : undefined,
        });
      }}
      errorMessage={!answerIsValid && showValidation ? requiredErrorMessage : undefined}
      followUpErrorMessage={
        !followUpIsValid && maximumCharacterLimitExceededBy > 0
          ? characterLimitErrorMessage
          : !followUpIsValid &&
              showValidation &&
              maximumCharacterLimitExceededBy === maximumCharacterLimitToRespect * -1
            ? requiredErrorMessage
            : undefined
      }
      dataTest={dataTest}
    />
  );

  return showRefreshedComponents ? likert : deprecatedLikert;
}
